import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Tooltip, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import Input from "../../../components/Input";
import Button from "../../../components/Button";
import Upload from "../../../components/Upload";
import Switch from "../../../components/Switch";
import messages from "../../../assets/locale/messages";
import {
  ENGLISH_LOOKUP_NAME_REGEX,
  ARABIC_LOOKUP_NAME_REGEX,
  ATLEAST_TWO_ENGLISH_CHARACTERS,
  ATLEAST_TWO_ARABIC_CHARACTERS,
  ONE_SPACE_ONLY,
} from "../../../utils/Patterns";
import { setPageMainTitle } from "../../../utils/Helpers";
import {
  uploadToS3,
  directories,
  generateUniqueFileName,
} from "../../../utils/Aws-S3";
import {
  editCategoryRequest,
  getCategoryDetailsRequest,
  getCategoryDetailsResponse,
  addCategoryRequest,
} from "../../../store/Categories/actions";

const AddEditServiceProviderCategories = () => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared, serviceProviderCategories, pagesTitles } = messages[lang];
  const { id } = useParams();
  const dispatch = useDispatch();
  const [img, setImg] = useState();
  // flag to upload image in edit mode if image changed only
  const [imgChanged, setImgChanged] = useState(false);
  const categoryDetails = useSelector(
    (state) => state.categories.categoryDetails
  );
  // if can_toggle_multi_session_allowed is false -> toggle is disabled
  const [canToggleMultiSession, setCanToggleMultiSession] = useState(true);

  useEffect(() => {
    setPageMainTitle("categoriesManagement");
  }, []);

  const {
    values,
    setFieldValue,
    setValues,
    handleSubmit,
    touched,
    errors,
    setFieldTouched,
    dirty,
    isValid,
  } = useFormik({
    initialValues: {
      title_en: "",
      title_ar: "",
      image: null,
      is_multi_session_allowed: false,
    },
    validationSchema: Yup.object({
      title_en: Yup.string()
        .required("enNameRequired")
        // .matches(NAME_REGEX, "nameNotFollowREGEX")
        .matches(
          ENGLISH_LOOKUP_NAME_REGEX,
          "englishLettersAndSpecialCharactersOnly"
        )
        .matches(ATLEAST_TWO_ENGLISH_CHARACTERS, "atleastTwoEnglishCharacters"),
      title_ar: Yup.string()
        .required("arNameRequired")
        // .matches(NAME_REGEX, "nameNotFollowREGEX")
        .matches(
          ARABIC_LOOKUP_NAME_REGEX,
          "arabicLettersAndSpecialCharactersOnly"
        )
        .matches(ATLEAST_TWO_ARABIC_CHARACTERS, "atleastTwoArabicCharacters"),
      image: Yup.mixed(),
    }),
    onSubmit: async ({ title_en, title_ar, image, is_multi_session_allowed }) => {
      let categoryData = { title_en, title_ar, is_multi_session_allowed };
      if (image) {
        if (imgChanged) {
          const data = await uploadToS3(
            image,
            generateUniqueFileName(image.name),
            directories.service_categories_logos
          );
          categoryData.image = data?.link;
        } else {
          categoryData.image = image;
        }
      } else {
        categoryData.image = null;
      }
      if (id) {
        dispatch(editCategoryRequest({ id, data: categoryData }));
      } else {
        dispatch(addCategoryRequest(categoryData));
      }
    },
  });

  useEffect(() => {
    if (id) {
      dispatch(getCategoryDetailsRequest(id));
    } else {
      setCanToggleMultiSession(true);
    }
    return () => {
      dispatch(getCategoryDetailsResponse({}));
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (id && categoryDetails) {
      setFieldTouched({
        title_en: true,
        title_ar: true,
        image: true,
        is_multi_session_allowed: true,
      });
      setValues({
        title_en: categoryDetails.title_en,
        title_ar: categoryDetails.title_ar,
        image: categoryDetails.image,
        is_multi_session_allowed: categoryDetails.is_multi_session_allowed ?? false,
      });
      setCanToggleMultiSession(
        categoryDetails.can_toggle_multi_session_allowed ?? true
      );
    }
  }, [categoryDetails, id, setFieldTouched, setValues]);

  return (
    <div className="add-edit-category-page">
      <div className="d-flex mb-4">
        <Typography variant="h4" sx={{ flexGrow: 1 }} className={`page-title`}>
          {id
            ? pagesTitles.serviceProviderCategoriesEdit
            : pagesTitles.serviceProviderCategoriesAdd}
        </Typography>
      </div>
      <form noValidate onSubmit={handleSubmit}>
        <div className="px-2 px-md-5 bg-white rounded-4">
          <div className="pt-4">
            <div className="row mt-4">
              <div className="col-12 col-md-6">
                <Input
                  id="title_en"
                  label={serviceProviderCategories.addCategory.labels.enTitle}
                  name="title_en"
                  type="text"
                  placeholder={
                    serviceProviderCategories.addCategory.placeholders.enTitle
                  }
                  required
                  onChange={(value) => {
                    if (ONE_SPACE_ONLY.test(value) || !value) {
                      setFieldTouched("title_en");
                      setFieldValue("title_en", value);
                    }
                  }}
                  value={values["title_en"]}
                  inputWrapperClass="mb-4"
                  labelClassName="text-dark font-medium mb-2"
                  isInputHasErr={!!(touched["title_en"] && errors["title_en"])}
                  errMsg={errors["title_en"]}
                />
              </div>
              <div className="col-12 col-md-6">
                <Input
                  id="title_ar"
                  label={serviceProviderCategories.addCategory.labels.arTitle}
                  name="title_ar"
                  type="text"
                  placeholder={
                    serviceProviderCategories.addCategory.placeholders.arTitle
                  }
                  required
                  onChange={(value) => {
                    if (ONE_SPACE_ONLY.test(value) || !value) {
                      setFieldTouched("title_ar");
                      setFieldValue("title_ar", value);
                    }
                  }}
                  value={values["title_ar"]}
                  inputWrapperClass="mb-4"
                  labelClassName="text-dark font-medium mb-2"
                  isInputHasErr={!!(touched["title_ar"] && errors["title_ar"])}
                  errMsg={errors["title_ar"]}
                />
              </div>
              <div className="col-12 col-md-6">
                <span className="text-dark font-medium">
                  {serviceProviderCategories.addCategory.labels.uploadLogo}
                </span>
                <Tooltip
                  title={
                    <span className="fsize-12">{shared.filesAccepted} </span>
                  }
                >
                  <span className="text-bg-primary-mid fsize-14 font-regular ms-2">
                    {shared.acceptedFormatLabel}
                  </span>
                </Tooltip>

                <Upload
                  label={
                    <div className="mt-2 pt-1 d-flex flex-column align-items-center">
                      <ImageOutlinedIcon fontSize="large" />
                      <span>
                        {
                          serviceProviderCategories.addCategory.labels
                            .uploadLogo
                        }
                      </span>
                    </div>
                  }
                  onChange={(img, value) => {
                    setImg(img);
                    setImgChanged(true);
                    setFieldValue("image", value);
                  }}
                  img={img || values["image"]}
                  name="image"
                />
              </div>
              <div className="col-12 col-md-6 mb-4">
                <div className="d-flex align-items-center">
                  <span className={`text-dark font-medium ${lang === "en" ? "me-3" : "ms-3"}`}>
                    {
                      serviceProviderCategories.addCategory.labels
                        .allowMultiSessions
                    }
                  </span>
                  <Tooltip
                    title={
                      <span className="fsize-12">
                        {
                          serviceProviderCategories.addCategory.tooltips
                            .toggleDisabled
                        }
                      </span>
                    }
                    arrow
                    placement="top"
                    disableHoverListener={canToggleMultiSession}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        cursor: canToggleMultiSession ? "pointer" : "not-allowed",
                      }}
                    >
                      <Switch
                        checked={values.is_multi_session_allowed}
                        onChange={(_, checked) => {
                          if (!canToggleMultiSession) return;

                          // Update local form state
                          setFieldValue("is_multi_session_allowed", checked);
                        }}
                        disabled={!canToggleMultiSession}
                        sxProps={{
                          margin: "0px !important",
                        }}
                      />
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="row mt-4 py-4 d-flex justify-content-center">
              <div className="col-4">
                <Button
                  onClick={() => {}}
                  type="submit"
                  className="btn-secondary"
                  block
                  label={shared.save}
                  labelClass="px-4 fsize-16 font-medium text-white"
                  disabled={!dirty || !isValid}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEditServiceProviderCategories;
